import { StateError } from '../errors';
import type { RuleModule } from '../engine/registry';
import { OK, reject, type BackRef, type GameRuntime, type GameState, type PhaseNode } from '../engine/state';
import { bagContains, bagTotal, bagIsCanonical, type CardId, type ResourceBag } from '../engine/gameDef';
import { shuffleInPlace } from '../board/generator';
import { inBuildWindow, payCost, poolRemaining } from './building';
import { edgePieces, notePiecePlaced, placementLegal } from '../engine/placement';
import { enterBanditMove } from './bandit';
import { enterPhase } from '../engine/phase';
import type { SeatIdx } from '../types';

// Advance cards (outline §10; plan §4.2 bag). H1-S2 lands the bag: deck composition
// scaled per band (plan §6.6 — per-card count comes from config.scaling when the card id
// is in its tables, else the def's literal count), shuffled ONCE at init (the plan §9.2 L1
// draw; TOP = last element). Buy/play actions land in H1-S7 (tests first).

export interface AdvanceSeatBag {
  cards: Array<{ card: CardId; boughtOnTurn: number }>;
  faceUpMilitia: number;
  /** Public only at GAME_OVER reveal (plan §8.4 full disclosure, D7). */
  revealedLandmarks: CardId[];
  /** The 1-per-turn allowance; reset by this module's own onWindowOpen hook (plan §5.2). */
  playedThisTurn: boolean;
}

export interface AdvanceBag {
  /** Ordered, server-private (plan §10.1); TOP = last element (plan §9.2 L1). */
  deck: CardId[];
  perSeat: AdvanceSeatBag[];
}

/** The effect key a card resolves to, from the def's decks (null for an unknown card). */
export function effectOf(env: GameRuntime, card: CardId): string | null {
  for (const deck of env.def.decks) {
    const hit = deck.cards.find((c) => c.card === card);
    if (hit) return hit.effect;
  }
  return null;
}

/** The window `seat` may play a card in right now, as the ref the effect returns to. */
export function playWindow(state: GameState, seat: SeatIdx): BackRef | null {
  const p = state.phase;
  if (p.t === 'PRE_ROLL' && p.seat === seat) return 'PRE_ROLL';
  if (p.t === 'ACTION' && p.seat === seat) return 'ACTION';
  if (p.t === 'PARTNER_ACTION' && p.seat === seat) return 'PARTNER_ACTION'; // outline §13.2: the partner plays cards; SBP never does
  return null;
}

/** One seat's advance-card state (H20-S0b: exported with its siblings, no longer closures). */
export function seatBag(state: GameState, seat: SeatIdx): AdvanceSeatBag {
  return (state.bags['advance-cards'] as AdvanceBag).perSeat[seat]!;
}

/**
 * Road Building's budget, CONTENT-BLIND (H20-S0d): the pieces left across every edge piece the
 * def declares. In the classic game that is the road pool alone — identical to the old read —
 * and in a def with a second edge piece (ships) a seat with one road and eight ships left still
 * gets both placements, where the road-only read gave it one.
 */
export function roadworksPool(state: GameState, env: GameRuntime, seat: SeatIdx): number {
  let n = 0;
  for (const p of edgePieces(env)) n += Math.max(0, poolRemaining(state, env, seat, p.id));
  return n;
}

/** Does ANY edge piece with pieces left have a legal site for `seat`? (asked of the placement registry) */
export function roadworksPlaceable(state: GameState, env: GameRuntime, seat: SeatIdx): boolean {
  const edges = [...env.board.edges.keys()];
  return edgePieces(env).some((p) => poolRemaining(state, env, seat, p.id) > 0 && edges.some((e) => placementLegal(state, env, seat, p.id, e)));
}

function backNodeOf(back: BackRef, seat: SeatIdx, roller: SeatIdx): PhaseNode {
  return back === 'PRE_ROLL' ? { t: 'PRE_ROLL', seat } : back === 'ACTION' ? { t: 'ACTION', seat } : { t: 'PARTNER_ACTION', roller, seat };
}

export const advanceCardsModule: RuleModule = {
  id: 'advance-cards',
  register(ctx) {
    ctx.configDefaults({ windfallTake: 2 }); // outline §10: take any 2 (config, not a literal)

    // ── PLAY_ADVANCE (outline §10 timing; effects militia/roadworks/windfall/levy) ──
    ctx.onWindowOpen((state, _env, seat) => {
      seatBag(state, seat).playedThisTurn = false; // the 1-per-turn allowance, per action window (plan §5.2)
    });

    // 'landmarks' VP source (outline §12): Landmark cards score from the HAND — hidden
    // until game end, but trueVp counts them (plan §7.5); excluded from vpPublic (§10.2)
    ctx.vpSource(
      'landmarks',
      (state, env, seat) => {
        const held = seatBag(state, seat).cards.filter((c) => effectOf(env, c.card) === 'landmark').length;
        return held * env.cfg.engine.vp.landmark;
      },
      { hidden: true },
    );

    ctx.action('PLAY_ADVANCE', {
      enumerate(state, env, seat) {
        if (playWindow(state, seat) === null) return null;
        const cards = [...new Set(seatBag(state, seat).cards.map((c) => c.card))];
        const out: Array<Record<string, unknown>> = [];
        for (const card of cards) {
          const effect = effectOf(env, card);
          if (effect === 'militia' || effect === 'roadworks') out.push({ card });
          else if (effect === 'levy') for (const r of env.def.resources) out.push({ card, resource: r.id });
          else if (effect === 'windfall') {
            const stocked = env.def.resources.filter((r) => (state.supply[r.id] ?? 0) > 0).map((r) => r.id);
            for (const a of stocked) {
              out.push({ card, take: { [a]: 1 } });
              for (const b of stocked) {
                const take: ResourceBag = a === b ? { [a]: 2 } : { [a]: 1, [b]: 1 };
                out.push({ card, take });
              }
            }
          }
        }
        return out;
      },
      validate(state, action, env) {
        const window = playWindow(state, action.seat);
        if (window === null) return reject('WRONG_PHASE');
        const sb = seatBag(state, action.seat);
        if (sb.playedThisTurn) return reject('CARD_ALREADY_PLAYED'); // outline §14.15
        const card = action.payload['card'];
        if (typeof card !== 'string') return reject('BAD_PAYLOAD');
        const held = sb.cards.find((c) => c.card === card && c.boughtOnTurn < state.turn.number);
        const heldAtAll = sb.cards.some((c) => c.card === card);
        if (!heldAtAll) return reject('CARD_NOT_HELD');
        if (!held) return reject('CARD_BOUGHT_THIS_TURN'); // outline §14.15 (landmark reveal is automatic — S10)
        const effect = effectOf(env, card);
        if (effect === null || effect === 'landmark') return reject('CARD_NOT_PLAYABLE'); // landmarks auto-count (outline §10)
        if (effect === 'roadworks') {
          // H20-S0d: any edge piece, legal through the registry — never a card that opens an
          // empty ROADWORKS window (D15, now for every edge piece the def has)
          if (roadworksPool(state, env, action.seat) < 1) return reject('POOL_EMPTY'); // D15
          if (!roadworksPlaceable(state, env, action.seat)) return reject('NO_LEGAL_PLACEMENT'); // D15
        } else if (effect === 'windfall') {
          const take = action.payload['take'];
          if (typeof take !== 'object' || take === null || Array.isArray(take)) return reject('BAD_PAYLOAD');
          const bag = take as ResourceBag;
          if (!bagIsCanonical(bag)) return reject('BAD_PAYLOAD');
          const stockTotal = env.def.resources.reduce((n, r) => n + (state.supply[r.id] ?? 0), 0);
          if (stockTotal === 0) return reject('SUPPLY_EMPTY'); // D14: never waste the card for nothing
          for (const [rid, n] of Object.entries(bag)) {
            if (!env.def.resources.some((r) => r.id === rid)) return reject('BAD_PAYLOAD');
            if (n > (state.supply[rid] ?? 0)) return reject('SUPPLY_SHORT');
          }
          const windfallTake = Number(env.composed.moduleConfig['advance-cards']?.['windfallTake'] ?? 2);
          if (bagTotal(bag) !== Math.min(windfallTake, stockTotal)) return reject('WRONG_TAKE_COUNT'); // D14 exact rule
        } else if (effect === 'levy') {
          const resource = action.payload['resource'];
          if (typeof resource !== 'string' || !env.def.resources.some((r) => r.id === resource)) return reject('BAD_PAYLOAD');
        }
        return OK;
      },
      apply(state, action, env, emit) {
        const seat = action.seat;
        const card = String(action.payload['card']);
        const effect = effectOf(env, card)!;
        const window = playWindow(state, seat)!;
        const sb = seatBag(state, seat);
        const idx = sb.cards.findIndex((c) => c.card === card && c.boughtOnTurn < state.turn.number);
        sb.cards.splice(idx, 1);
        sb.playedThisTurn = true;
        emit({ kind: 'ADVANCE_PLAYED', vis: 'public', seat, card });
        if (effect === 'militia') {
          sb.faceUpMilitia += 1; // face-up permanently; drives Muster (outline §10/§14.14)
          enterBanditMove(state, env, seat, 'MILITIA', window, emit); // NO discard step (outline §14.2)
        } else if (effect === 'roadworks') {
          const remaining = Math.min(2, roadworksPool(state, env, seat)); // min-1 guaranteed by validate (D15)
          enterPhase(state, env, { t: 'ROADWORKS', seat, remaining, back: window }, emit);
        } else if (effect === 'windfall') {
          const take = action.payload['take'] as ResourceBag;
          for (const [rid, n] of Object.entries(take)) {
            state.supply[rid] = (state.supply[rid] ?? 0) - n;
            state.seats[seat]!.hand[rid] = (state.seats[seat]!.hand[rid] ?? 0) + n;
          }
          emit({ kind: 'WINDFALL_TAKEN', vis: 'public', seat, bag: { ...take } });
        } else if (effect === 'levy') {
          const resource = String(action.payload['resource']);
          const amounts: Record<number, number> = {};
          state.seats.forEach((other, i) => {
            if (i === seat) return;
            const n = other.hand[resource] ?? 0;
            if (n > 0) {
              delete other.hand[resource];
              state.seats[seat]!.hand[resource] = (state.seats[seat]!.hand[resource] ?? 0) + n;
              amounts[i] = n;
            }
          });
          emit({ kind: 'LEVY_COLLECTED', vis: 'public', seat, resource, amounts }); // Supply untouched (outline §14.16)
        }
      },
    });

    ctx.phaseState('ROADWORKS', { eligibleWinner: (n) => (n.t === 'ROADWORKS' ? n.seat : null), timeoutKey: 'ROADWORKS' });
    ctx.action('ROADWORKS_PLACE', {
      // H20-S0d: `{ edge, piece? }` — absent means the def's FIRST edge piece, so the classic
      // payload (and every logged classic action) is unchanged; another edge piece names itself.
      enumerate(state, env, seat) {
        if (state.phase.t !== 'ROADWORKS' || state.phase.seat !== seat) return null;
        const edges = [...env.board.edges.keys()];
        return edgePieces(env).flatMap((p, i) => edges.map((edge) => (i === 0 ? { edge } : { edge, piece: p.id })));
      },
      validate(state, action, env) {
        if (state.phase.t !== 'ROADWORKS') return reject('WRONG_PHASE');
        if (state.phase.seat !== action.seat) return reject('NOT_YOUR_TURN');
        const edge = action.payload['edge'];
        if (typeof edge !== 'string') return reject('BAD_PAYLOAD');
        const pieces = edgePieces(env);
        const named = action.payload['piece'];
        if (named !== undefined && (typeof named !== 'string' || !pieces.some((p) => p.id === named))) return reject('BAD_PAYLOAD');
        const piece = (named as string | undefined) ?? pieces[0]?.id;
        if (piece === undefined) return reject('BAD_PAYLOAD');
        if (!placementLegal(state, env, action.seat, piece, edge)) return reject('ILLEGAL_PLACEMENT'); // normal rules (outline §14.17)
        if (poolRemaining(state, env, action.seat, piece) < 1) return reject('POOL_EMPTY');
        return OK;
      },
      apply(state, action, env, emit) {
        if (state.phase.t !== 'ROADWORKS') return;
        const edge = String(action.payload['edge']);
        const piece = typeof action.payload['piece'] === 'string' ? action.payload['piece'] : edgePieces(env)[0]!.id;
        const { back, remaining: before } = state.phase;
        state.board.edges[edge] = { piece, owner: action.seat }; // FREE (outline §10)
        emit({ kind: 'BUILT', vis: 'public', seat: action.seat, piece, at: edge, free: true });
        // the placement's own consequences land FIRST (a fog reveal, H20-S6), so whether the
        // card continues is decided against the board it revealed — a reveal can open the
        // only continuation there is
        notePiecePlaced(state, env, action.seat, piece, edge, emit);
        const remaining = before - 1;
        const canContinue = remaining > 0 && roadworksPool(state, env, action.seat) > 0 && roadworksPlaceable(state, env, action.seat);
        if (canContinue) {
          state.phase = { t: 'ROADWORKS', seat: action.seat, remaining, back };
        } else {
          if (remaining > 0) {
            // cut short (pool dry / no legal edge) rather than finished — narrate why (plan §7.4)
            emit({ kind: 'AUTO_RESOLVED', vis: 'public', seat: action.seat, what: 'ROADWORKS_CUT_SHORT' });
          }
          enterPhase(state, env, backNodeOf(back, action.seat, state.turn.roller), emit); // done / pool-dry / no legal edge ⇒ auto-return (plan §7.4)
        }
      },
    });

    ctx.action('BUY_ADVANCE', {
      // Buy any number per turn from the top of the face-down deck (outline §7/§14.15);
      // the deck never replenishes. PLAY_ADVANCE lands in H1-S7.
      enumerate(state, _env, seat) {
        return inBuildWindow(state, seat) ? [{}] : null;
      },
      validate(state, action, env) {
        if (!inBuildWindow(state, action.seat)) return reject('WRONG_PHASE');
        const bag = state.bags['advance-cards'] as AdvanceBag;
        if (bag.deck.length === 0) return reject('DECK_EMPTY');
        if (!bagContains(state.seats[action.seat]!.hand, env.def.costs['advance-card'] ?? {})) return reject('CANNOT_AFFORD');
        return OK;
      },
      apply(state, action, env, emit) {
        const bag = state.bags['advance-cards'] as AdvanceBag;
        payCost(state, action.seat, env.def.costs['advance-card'] ?? {});
        const card = bag.deck.pop()!; // TOP = last element (plan §9.2 L1)
        bag.perSeat[action.seat]!.cards.push({ card, boughtOnTurn: state.turn.number });
        emit({ kind: 'CARD_BOUGHT', vis: 'public', seat: action.seat, deckRemaining: bag.deck.length }); // identity stays hidden (plan §8.4)
      },
    });

    ctx.bag({
      init(env, rng) {
        const deckDef = env.def.decks[0];
        if (!deckDef) throw new StateError('advance-cards: the def has no deck');
        const scaledOf = env.counts.deck;
        const deck: CardId[] = [];
        for (const c of deckDef.cards) {
          const n = Object.prototype.hasOwnProperty.call(scaledOf, c.card) ? scaledOf[c.card]! : c.count;
          for (let i = 0; i < n; i++) deck.push(c.card);
        }
        shuffleInPlace(deck, rng); // plan §9.2 L1 — the only bag-init draw
        return {
          deck,
          perSeat: Array.from({ length: env.seatsCount }, () => ({
            cards: [],
            faceUpMilitia: 0,
            revealedLandmarks: [],
            playedThisTurn: false,
          })),
        } satisfies AdvanceBag;
      },
      clone(bag) {
        const b = bag as AdvanceBag;
        return {
          deck: [...b.deck],
          perSeat: b.perSeat.map((s) => ({
            cards: s.cards.map((c) => ({ card: c.card, boughtOnTurn: c.boughtOnTurn })),
            faceUpMilitia: s.faceUpMilitia,
            revealedLandmarks: [...s.revealedLandmarks],
            playedThisTurn: s.playedThisTurn,
          })),
        } satisfies AdvanceBag;
      },
    });
  },
};
